import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { planosDisponiveis, fetchPlanosFromDB, PlanInfo } from '@/lib/planConfig';
import { fmtMoeda } from '@/pages/plan/shared';

// Texto da landing.
//
// Nao e mock: e o conteudo da propria pagina, e mora junto dela. Estava em
// `mocks/home`, no meio de dados falsos de condominio e de plano, o que fazia
// parecer que um dia viria do banco. Se um dia vier de CMS, e este bloco que
// sai — os planos ao lado ja vem do catalogo.
const HERO = {
  headline: 'Centralize a conformidade e a operação dos condomínios em um só lugar.',
  subheadline:
    'A Central Check ajuda administradoras e síndicos a controlar imóveis, alvarás, manutenções, infraestrutura, profissionais e alertas críticos com visão clara de risco e saúde operacional.',
  ctaPrimary: 'Conhecer planos',
  ctaSecondary: 'Ver demonstração',
};

const RECURSOS = [
  {
    icon: 'ri-building-line',
    title: 'Gestão de condomínios e torres',
    description:
      'Organize condomínios, torres, blocos e unidades em uma estrutura única para a administradora.',
  },
  {
    icon: 'ri-file-shield-line',
    title: 'Controle de alvarás e documentos',
    description:
      'Acompanhe vencimentos, status, responsáveis e alertas antes que uma pendência vire risco operacional.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Infraestrutura e manutenções',
    description:
      'Monitore elevadores, piscinas, sistemas de incêndio e itens críticos com histórico de manutenção.',
  },
  {
    icon: 'ri-user-settings-line',
    title: 'Profissionais e conformidade',
    description:
      'Tenha visibilidade sobre equipes internas, terceiros, treinamentos, certificações e pendências.',
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Indicadores de saúde do condomínio',
    description:
      'Veja KPIs de segurança, conformidade e operação em um painel claro para decisões rápidas.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Segurança e conformidade',
    description:
      'Controle de acesso por perfil, histórico de alterações e estrutura pensada para múltiplos clientes.',
  },
];

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('opacity-100', 'translate-y-0');
          el.classList.remove('opacity-0', 'translate-y-6');
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div className="group bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1">
      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
        <i className={`${icon} text-primary-600 text-lg`}></i>
      </div>
      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">{title}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed">{description}</p>
    </div>
  );
}

function PlanCard({
  name,
  price,
  period,
  description,
  features,
  cta,
  highlighted,
  badge,
}: {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  highlighted: boolean;
  badge?: string;
}) {
  return (
    <div
      className={`relative rounded-lg p-5 md:p-6 flex flex-col ${
        highlighted
          ? 'bg-primary-500 border-2 border-primary-500 text-background-50'
          : 'bg-background-50 border border-background-200/70 text-foreground-900'
      }`}
    >
      {badge && (
        <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-xs font-semibold bg-accent-500 text-background-50">
          {badge}
        </span>
      )}
      <h3
        className={`text-sm font-semibold mb-1 ${highlighted ? 'text-background-100' : 'text-foreground-700'}`}
      >
        {name}
      </h3>
      <div className="flex items-baseline gap-1 mb-3">
        <span
          className={`text-2xl md:text-3xl font-bold ${highlighted ? 'text-background-50' : 'text-foreground-900'}`}
        >
          {price}
        </span>
        {period && (
          <span
            className={`text-sm ${highlighted ? 'text-background-200' : 'text-foreground-600'}`}
          >
            {period}
          </span>
        )}
      </div>
      <p
        className={`text-sm mb-5 leading-relaxed ${highlighted ? 'text-background-200' : 'text-foreground-600'}`}
      >
        {description}
      </p>
      <ul className="flex flex-col gap-2.5 mb-6 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <i
              className={`ri-check-line mt-0.5 ${highlighted ? 'text-accent-400' : 'text-primary-500'}`}
            ></i>
            <span className={highlighted ? 'text-background-100' : 'text-foreground-700'}>{f}</span>
          </li>
        ))}
      </ul>
      <button
        className={`w-full py-2.5 px-4 rounded-md text-sm font-semibold whitespace-nowrap transition-all ${
          highlighted
            ? 'bg-background-50 text-primary-600 hover:bg-background-100'
            : 'bg-primary-500 text-background-50 hover:bg-primary-600'
        }`}
      >
        {cta}
      </button>
    </div>
  );
}

export default function HomePage() {
  const featuresRef = useReveal();
  const plansRef = useReveal();
  const ctaRef = useReveal();

  // Em estado: a lista do modulo muda por fora do React (ver planConfig).
  const [planos, setPlanos] = useState<PlanInfo[]>(planosDisponiveis);

  useEffect(() => {
    let ativo = true;

    fetchPlanosFromDB().then((lista) => {
      if (ativo) setPlanos(lista);
    });

    return () => {
      ativo = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[620px] md:min-h-[720px] flex items-center overflow-hidden">
        {/* Gradiente no lugar da imagem: a anterior era gerada sob demanda pela
            API do Readdy, e a landing dependia de um serviço externo para
            renderizar o próprio topo. */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(1200px 600px at 20% 20%, rgba(56,132,255,0.35), transparent 60%), radial-gradient(900px 500px at 80% 30%, rgba(234,179,8,0.25), transparent 55%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #0b1220 100%)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/60 to-foreground-950/30"></div>

        <div className="relative z-10 w-full px-6 md:px-10 py-24 md:py-32">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-7xl mx-auto">
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-5">
                {HERO.headline}
              </h1>
              <p className="text-base md:text-lg text-background-200/90 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                {HERO.subheadline}
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
                <a
                  href="#planos"
                  className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  {HERO.ctaPrimary}
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a
                  href="#"
                  className="px-6 py-3 rounded-md border border-background-400/50 text-background-50 text-sm font-semibold hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  {HERO.ctaSecondary}
                </a>
              </div>
            </div>

            {/* Dashboard Mockup */}
            <div className="flex-1 w-full max-w-md lg:max-w-lg">
              <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
                  <span className="text-sm font-semibold text-foreground-800">
                    Painel de saúde operacional
                  </span>
                  <i className="ri-checkbox-circle-line text-primary-500"></i>
                </div>
                <div className="p-4 md:p-5">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-background-100 rounded-lg p-3">
                      <p className="text-xs text-foreground-500 mb-1">Condomínios</p>
                      <p className="text-xl font-bold text-primary-600">24</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-3">
                      <p className="text-xs text-foreground-500 mb-1">Imóveis</p>
                      <p className="text-xl font-bold text-primary-600">1.842</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-3">
                      <p className="text-xs text-foreground-500 mb-1">Saúde</p>
                      <p className="text-xl font-bold text-accent-600">91%</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 bg-background-100 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-accent-50 flex items-center justify-center shrink-0">
                        <i className="ri-error-warning-line text-accent-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-700">AVCB vence em 15 dias</span>
                    </div>
                    <div className="flex items-center gap-3 bg-background-100 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-accent-50 flex items-center justify-center shrink-0">
                        <i className="ri-error-warning-line text-accent-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-700">
                        Elevador Torre B com manutenção agendada
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-background-100 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-accent-50 flex items-center justify-center shrink-0">
                        <i className="ri-error-warning-line text-accent-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-700">
                        Treinamento de portaria pendente
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="recursos" className="py-16 md:py-24 px-6 md:px-10 bg-background-50">
        <div
          ref={featuresRef}
          className="max-w-6xl mx-auto opacity-0 translate-y-6 transition-all duration-700"
        >
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              Recursos para reduzir risco e ganhar previsibilidade
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              Da unidade cadastrada ao alvará vencendo, a Central Check transforma dados
              operacionais em rotinas claras para a equipe.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {RECURSOS.map((f) => (
              <FeatureCard
                key={f.title}
                icon={f.icon}
                title={f.title}
                description={f.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="planos" className="py-16 md:py-24 px-6 md:px-10 bg-background-100">
        <div
          ref={plansRef}
          className="max-w-5xl mx-auto opacity-0 translate-y-6 transition-all duration-700"
        >
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              Planos para cada tamanho de operação
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              A cobrança acompanha a quantidade de imóveis ativos, mantendo previsibilidade para
              síndicos e administradoras.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {planos.map((plano) => (
              <PlanCard
                key={plano.id}
                name={plano.nome}
                price={fmtMoeda(plano.preco)}
                period="/mês"
                description={plano.descricao}
                features={plano.recursos.slice(0, 4)}
                cta="Solicitar contratação"
                highlighted={plano.destaque}
                badge={plano.popular ? 'Mais escolhido' : undefined}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-6 md:px-10 bg-primary-500">
        <div
          ref={ctaRef}
          className="max-w-5xl mx-auto opacity-0 translate-y-6 transition-all duration-700"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-3">
                Pronto para profissionalizar a gestão dos seus condomínios?
              </h2>
              <p className="text-base text-background-200 leading-relaxed max-w-xl">
                Comece com uma demonstração e veja como a Central Check organiza pendências, reduz
                esquecimentos e melhora a tomada de decisão.
              </p>
            </div>
            <a
              href="#"
              className="shrink-0 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              Entrar na demonstração
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
